// LocalStorage-backed session state for the external Benji widget.
//
// Persists sessionId + threadId + minimal qualifying data + UI flags so a
// page refresh doesn't lose the conversation context. Each browser gets a
// stable sessionId (cuid-like nanostring) so the backend can match returning
// visitors against their existing ChatThread.

const STORAGE_KEY = "ccb-benji-session";

export interface WidgetMessage {
  id: string;
  role: "user" | "assistant" | "system_event";
  text: string;
  streaming?: boolean;
  chips?: string[];
  /** "up" | "down" | null — set when the prospect rates the bot reply. */
  feedback?: "up" | "down" | null;
}

export interface QualifyingData {
  eventType?: string;
  guests?: number;
  eventDate?: string;
  locationCity?: string;
  serviceWindow?: string;
  notes?: string;
}

export interface ExperimentState {
  /** Locked-in CTA-copy variant (A-F). Computed on first launcher mount. */
  variant: string | null;
  /** Locked-in launcher-microinteraction variant (A/B/C). Computed in the
   * same first-mount pass as `variant`, salted independently for orthogonality. */
  interactionVariant: string | null;
  /** Once-per-session guards so we don't double-count tracking events. */
  trackedShown: boolean;
  trackedOpened: boolean;
  trackedMessaged: boolean;
  trackedCaptured: boolean;
}

export interface SessionState {
  sessionId: string;
  threadId: string | null;
  messages: WidgetMessage[];
  qualifying: QualifyingData;
  capturedLeadId: string | null;
  uiOpen: boolean;
  experiment: ExperimentState;
}

function randomId(): string {
  // 24-char base36 — collision-safe for our volume.
  return Array.from(crypto.getRandomValues(new Uint8Array(18)))
    .map((b) => (b % 36).toString(36))
    .join("");
}

export function loadSession(): SessionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SessionState>;
      if (parsed && typeof parsed.sessionId === "string") {
        // Backfill the experiment block for sessions persisted before the
        // A/B-test code shipped, so we don't crash dereferencing undefined.
        if (!parsed.experiment) {
          (parsed as SessionState).experiment = defaultExperiment();
        } else if ((parsed.experiment as ExperimentState).interactionVariant === undefined) {
          // Sessions persisted before the launcher-interaction A/B/C shipped
          // — backfill the new field as null so the first-mount path can
          // assign and lock a variant. (null + write means "compute fresh".)
          (parsed.experiment as ExperimentState).interactionVariant = null;
        }
        return parsed as SessionState;
      }
    }
  } catch {
    /* corrupted or unavailable */
  }
  return freshSession();
}

/** A brand-new, never-persisted SessionState. Reused by the team "New chat"
 * button and the `?ccb-benji-reset=1` URL trigger so they always produce a
 * fully-initialized state — no half-cleared experiment block, no leftover
 * threadId, no `uiOpen: true` half-state. */
export function freshSession(): SessionState {
  return {
    sessionId: randomId(),
    threadId: null,
    messages: [],
    qualifying: {},
    capturedLeadId: null,
    uiOpen: false,
    experiment: defaultExperiment(),
  };
}

function defaultExperiment(): ExperimentState {
  return {
    variant: null,
    interactionVariant: null,
    trackedShown: false,
    trackedOpened: false,
    trackedMessaged: false,
    trackedCaptured: false,
  };
}

export function saveSession(state: SessionState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* full quota or disabled */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Read the magic-link preview cookie (`ccb_benji_preview`). Returns "" if absent. */
export function readPreviewCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)ccb_benji_preview=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : "";
}
